import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/d88baf08cf9aab8cd54854b5018978ec360b4e238c0fbb7dabe14674d502f36a.png';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    const routeRedirect = `${location.pathname}${location.search}`;
    const queryRedirect = new URLSearchParams(location.search).get('redirect');
    const safeQueryRedirect = queryRedirect && queryRedirect.startsWith('/') ? queryRedirect : null;
    const safeRouteRedirect = !['/login', '/register'].includes(location.pathname)
      ? routeRedirect
      : null;
    const effectiveRedirect = safeQueryRedirect || safeRouteRedirect;

    if (!sessionId) {
      const loginPath = effectiveRedirect
        ? `/login?redirect=${encodeURIComponent(effectiveRedirect)}`
        : '/login';
      navigate(loginPath);
      return;
    }

    (async () => {
      try {
        const res = await api.post('/auth/google-callback', { session_id: sessionId });
        setUser(res.data);
        window.history.replaceState(null, '', window.location.pathname);
        if (effectiveRedirect && res.data?.role !== 'admin') {
          navigate(effectiveRedirect, { replace: true });
          return;
        }
        navigate(res.data?.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
      } catch {
        const loginPath = effectiveRedirect
          ? `/login?redirect=${encodeURIComponent(effectiveRedirect)}`
          : '/login';
        navigate(loginPath, { replace: true });
      }
    })();
  }, [location.pathname, location.search, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="auth-callback">
      <div className="px-4 sm:px-6 pt-4 sm:pt-6">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2"
          data-testid="auth-callback-home-logo"
        >
          <img src={LOGO_URL} alt="Nest Geneva" className="h-8 sm:h-10 w-auto" />
          <span className="font-heading text-base sm:text-lg font-semibold text-[#0B3D2E]">Nest Geneva</span>
        </button>
      </div>
      <div className="min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm sm:text-base text-[#5a5a5a]">Completing sign in...</p>
        </div>
      </div>
    </div>
  );
}
